import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../lib/i18n';

const LOGO = "https://static.prod-images.emergentagent.com/jobs/6554b71a-e92e-45d6-a2ea-28b8d837c5dd/images/05ed22a83a162dbd4a68bdfa54453a7a8a9f16d8e3a633061831e14ecf8e4761.jpeg";

const Navbar = ({ transparent = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const isTransparent = transparent && !scrolled && !menuOpen;

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <motion.nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isTransparent ? 'bg-transparent' : 'bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-sm'
      }`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <img src={LOGO} alt="HYGAD" className="h-10 w-auto object-contain" />
        </Link>

        {/* Desktop links */}
        <div className={`hidden md:flex items-center gap-8 text-sm font-medium transition-colors duration-300 ${
          isTransparent ? 'text-white/90' : 'text-navy'
        }`}>
          <Link to="/search?experience=stay" data-testid="nav-stay"
            className="hover:text-gold transition-colors duration-200 relative group">
            {t('nav.stay')}
            <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-gold group-hover:w-full transition-all duration-300" />
          </Link>
          <Link to="/search?experience=celebrate" data-testid="nav-celebrate"
            className="hover:text-gold transition-colors duration-200 relative group">
            {t('nav.celebrate')}
            <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-gold group-hover:w-full transition-all duration-300" />
          </Link>
        </div>

        {/* Auth buttons */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-3">
              <Link
                to={user.role === 'Admin' ? '/admin' : user.role === 'Customer' ? '/customer' : '/partner'}
                data-testid="nav-dashboard"
                className="text-sm font-medium text-navy bg-gold/10 hover:bg-gold/20 px-4 py-2 rounded-lg transition-colors duration-200"
              >
                {user.role === 'Customer' ? 'My Bookings' : t('nav.dashboard')}
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-signout"
                className="text-sm text-gray-500 hover:text-red-500 transition-colors duration-200"
              >
                {t('nav.sign_out')}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/auth" data-testid="nav-signin"
                className={`text-sm font-medium transition-colors duration-200 ${
                  isTransparent ? 'text-white/90 hover:text-gold' : 'text-navy hover:text-gold'
                }`}>
                {t('nav.sign_in')}
              </Link>
              <Link to="/auth?tab=partner" data-testid="nav-list-property"
                className="bg-navy hover:bg-navy-light text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors duration-200 border border-gold/20">
                {t('nav.list_property')}
              </Link>
            </div>
          )}
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="nav-mobile-menu"
          className={`md:hidden p-2 transition-colors ${isTransparent ? 'text-white' : 'text-navy'}`}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="px-6 py-5 flex flex-col gap-5">
              <Link to="/search?experience=stay" className="text-sm font-medium text-navy hover:text-gold transition-colors">
                {t('nav.stay')}
              </Link>
              <Link to="/search?experience=celebrate" className="text-sm font-medium text-navy hover:text-gold transition-colors">
                {t('nav.celebrate')}
              </Link>
              <div className="border-t border-gray-100 pt-4">
                {user ? (
                  <>
                    <Link to={user.role === 'Admin' ? '/admin' : user.role === 'Customer' ? '/customer' : '/partner'} className="block text-sm font-medium text-navy mb-3">
                      {user.role === 'Customer' ? 'My Bookings' : t('nav.dashboard')} ({user.name})
                    </Link>
                    <button onClick={handleLogout} className="text-sm text-red-500">{t('nav.sign_out')}</button>
                  </>
                ) : (
                  <div className="flex flex-col gap-3">
                    <Link to="/auth" className="text-sm font-medium text-navy">{t('nav.sign_in')}</Link>
                    <Link to="/auth?tab=partner" className="bg-navy text-white text-sm font-medium px-4 py-2.5 rounded-lg text-center">
                      {t('nav.list_property')}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
